import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { TenantTypeCode } from '@axioma/shared';

interface CurrentUser {
  id: string;
  email: string;
  name: string;
  twoFactorEnabled: boolean;
  oabNumber?: string | null;
  oabState?: string | null;
}

interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  type: TenantTypeCode;
  role: string;
}

interface AuthState {
  /** JWT pendente após magic link, antes do 2FA. */
  pendingToken: string | null;
  /** JWT completo (2FA verified). */
  token: string | null;
  user: CurrentUser | null;
  tenants: TenantSummary[];
  currentTenantId: string | null;

  setPendingToken: (token: string, user: CurrentUser) => void;
  setToken: (token: string) => void;
  setProfile: (data: {
    user: CurrentUser;
    tenants: TenantSummary[];
    currentTenantId: string | null;
  }) => void;
  setCurrentTenant: (tenantId: string) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      pendingToken: null,
      token: null,
      user: null,
      tenants: [],
      currentTenantId: null,

      setPendingToken: (token, user) => set({ pendingToken: token, user }),
      setToken: (token) => set({ token, pendingToken: null }),
      setProfile: ({ user, tenants, currentTenantId }) => set({ user, tenants, currentTenantId }),
      setCurrentTenant: (tenantId) => set({ currentTenantId: tenantId }),
      clear: () =>
        set({
          pendingToken: null,
          token: null,
          user: null,
          tenants: [],
          currentTenantId: null,
        }),
    }),
    {
      name: 'axioma-auth',
      partialize: (state) => ({
        // Persistir apenas dados não-sensíveis. Token fica em memória.
        user: state.user,
        currentTenantId: state.currentTenantId,
        tenants: state.tenants,
      }),
    },
  ),
);
